<script>
  import Icon from "./Icon.svelte";

  /**
   * Reusable Select Component
   * @prop {string} id - Select ID
   * @prop {string} label - Label text
   * @prop {Array} options - Array of options (strings or {value, label} objects)
   * @prop {string} value - Selected value (bindable)
   * @prop {string} placeholder - Placeholder text
   * @prop {boolean} required - Required field
   * @prop {boolean} disabled - Disabled state
   * @prop {string} error - Error message
   * @prop {string} helpText - Help text
   * @prop {string} icon - Optional icon name (Lucide icon)
   */
  let {
    id = "",
    label = "",
    options = [],
    value = $bindable(""),
    placeholder = "Select an option",
    required = false,
    disabled = false,
    error = "",
    helpText = "",
    icon = "",
    onchange = undefined,
  } = $props();
</script>

<div class="form-group" class:has-error={error}>
  {#if label}
    <label for={id} class="form-label">
      {#if icon}<Icon name={icon} size={16} className="field-icon" />{/if}
      {label}
      {#if required}<span class="required">*</span>{/if}
    </label>
  {/if}

  <div class="select-wrapper">
    <select
      {id}
      class="form-select"
      bind:value
      {required}
      {disabled}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error
        ? `${id}-error`
        : helpText
          ? `${id}-help`
          : undefined}
      {onchange}
    >
      <option value="" disabled>{placeholder}</option>
      {#each options as option}
        {#if typeof option === "string"}
          <option value={option}>{option}</option>
        {:else}
          <option value={option.value}>{option.label}</option>
        {/if}
      {/each}
    </select>
    <span class="select-arrow">▼</span>
  </div>

  {#if error}
    <p id="{id}-error" class="form-error">{error}</p>
  {:else if helpText}
    <p id="{id}-help" class="form-help">{helpText}</p>
  {/if}
</div>

<style>
  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .form-label {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-600);
    letter-spacing: var(--tracking-wide);
  }

  .required {
    color: var(--color-primary-500);
    margin-left: var(--space-1);
  }

  .select-wrapper {
    position: relative;
  }

  .form-select {
    width: 100%;
    padding: var(--space-3) var(--space-10) var(--space-3) var(--space-4);
    font-size: var(--text-base);
    color: var(--color-gray-800);
    background: var(--glass-bg);
    backdrop-filter: blur(var(--blur-sm));
    -webkit-backdrop-filter: blur(var(--blur-sm));
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-lg);
    appearance: none;
    cursor: pointer;
    transition:
      border-color var(--transition-fast),
      box-shadow var(--transition-fast),
      background var(--transition-fast);
    min-height: var(--touch-target-min);
  }

  .form-select:hover:not(:disabled) {
    border-color: var(--color-gray-300);
    background: var(--glass-bg-dark);
  }

  .form-select:focus {
    outline: none;
    border-color: var(--color-primary-400);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    background: white;
  }

  .form-select:disabled {
    background: var(--color-gray-100);
    color: var(--color-gray-500);
    cursor: not-allowed;
    backdrop-filter: none;
  }

  .select-arrow {
    position: absolute;
    right: var(--space-4);
    top: 50%;
    transform: translateY(-50%);
    font-size: var(--text-xs);
    color: var(--color-gray-400);
    pointer-events: none;
    transition: color var(--transition-fast);
  }

  .form-select:focus + .select-arrow {
    color: var(--color-primary-500);
  }

  .has-error .form-select {
    border-color: var(--color-error-400);
    background: rgba(254, 242, 242, 0.5);
  }

  .has-error .form-select:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }

  .form-error {
    font-size: var(--text-sm);
    color: var(--color-error-600);
    margin: 0;
  }

  .form-help {
    font-size: var(--text-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
</style>
